import { useMemo } from 'react';
import { useUserRole } from './useUserRole';
import { useSystemConfig } from '@/contexts/SettingsContext';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from './useMediaQuery';

/**
 * Interface for filter visibility permissions
 */
export interface FilterPermissions {
  showCompanyFilter: boolean;      // Separate control for COMPANY filter
  showGroupFilter: boolean;         // Separate control for GROUP/PRODUCTION filter
  showIndustryFilter: boolean;     // Industry filter - consolidated logic
  showModelFilter: boolean;
  showStatusFilter: boolean;
  loading: boolean;
}

/**
 * Options for useFilterPermissions hook
 */
export interface FilterPermissionsOptions {
  /** Number of industries with active units (for auto-hide logic) */
  activeIndustryCount?: number;
}

/**
 * Hook to determine which filters should be visible based on user role (RBAC),
 * system configuration, and data conditions.
 *
 * Industry (IndustryType) Filter Visibility Logic:
 * 1. RBAC: Superadmin and admin can see (security boundary)
 * 2. Desktop: Always show for superadmins/admins (no auto-hide)
 * 3. Mobile only: Apply auto-hide rules (config, page, data conditions)
 *
 * Other filters use simple role-based rules:
 * - **superadmin/admin**: All filters visible
 * - **company_admin/group_admin**: Group, Model, Status filters
 * - **user**: Model and Status only
 *
 * Note: Filter visibility is for UX clarity, not security.
 * Row Level Security (RLS) on the backend handles actual data restrictions.
 *
 * @param options - Optional parameters for data-driven visibility
 * @returns {FilterPermissions} Object with boolean flags for each filter type
 */
export function useFilterPermissions(options?: FilterPermissionsOptions): FilterPermissions {
  const { role, isSuperadmin, isAdmin, loading: roleLoading } = useUserRole();
  const { systemConfig } = useSystemConfig();
  const location = useLocation();
  const isMobile = useIsMobile();

  const activeIndustryCount = options?.activeIndustryCount ?? Infinity;
  const isReportsPage = location.pathname.startsWith('/reports');

  const permissions = useMemo(() => {
    // While loading, show no filters to prevent flash
    if (roleLoading || !role) {
      return {
        showCompanyFilter: false,
        showGroupFilter: false,
        showIndustryFilter: false,
        showModelFilter: false,
        showStatusFilter: false,
      };
    }

    // ==== Industry (IndustryType) Filter - Consolidated Logic ====
    let showIndustryFilter = false;

    // RULE 1: RBAC - superadmins and admins can see
    if (isSuperadmin || isAdmin) {
      // RULE 2: Desktop always shows for superadmins/admins
      if (!isMobile) {
        showIndustryFilter = true;
      }
      // RULE 3: Mobile - apply auto-hide rules
      else {
        // If auto-hide disabled, show
        if (!systemConfig.autoHideIndustryFilter) {
          showIndustryFilter = true;
        }
        // Reports page always shows filter
        else if (isReportsPage) {
          showIndustryFilter = true;
        }
        // Auto-hide when ≤1 industry
        else if (activeIndustryCount > 1) {
          showIndustryFilter = true;
        }
        // Otherwise: hide (≤1 industry with auto-hide ON)
      }
    }

    // ==== Other filters - Simple RBAC rules ====

    // User role: read-only access, minimal filtering capabilities
    if (role === 'user') {
      return {
        showCompanyFilter: false,
        showGroupFilter: false,
        showIndustryFilter: false, // Never for regular users
        showModelFilter: true,
        showStatusFilter: true,
      };
    }

    // Company admin and Group admin: Can see groups, but not company selector
    if (role === 'company_admin' || role === 'group_admin') {
      return {
        showCompanyFilter: false,
        showGroupFilter: true,
        showIndustryFilter, // Uses consolidated logic above
        showModelFilter: true,
        showStatusFilter: true,
      };
    }

    // Admin and superadmin: full filter access
    return {
      showCompanyFilter: true,
      showGroupFilter: true,
      showIndustryFilter, // Uses consolidated logic above
      showModelFilter: true,
      showStatusFilter: true,
    };
  }, [role, isSuperadmin, isAdmin, roleLoading, systemConfig.autoHideIndustryFilter, isReportsPage, activeIndustryCount, isMobile]);

  return {
    ...permissions,
    loading: roleLoading,
  };
}
